export type AutomationRunProgressStatus =
  | 'running'
  | 'done'
  | 'error'
  | 'finished';

/** Per-step progress pushed from the main process while a run is in flight. */
export interface AutomationRunProgress {
  automationId: number;
  runId: string;
  nodeId?: string;
  status: AutomationRunProgressStatus;
  durationMs?: number;
  message?: string;
}

export interface WorkflowRunState {
  running: boolean;
  runId: string | null;
  activeNodeId: string | null;
  completed: Record<string, number>;
  failedNodeId: string | null;
}

export const emptyRunState = (): WorkflowRunState => ({
  running: false,
  runId: null,
  activeNodeId: null,
  completed: {},
  failedNodeId: null,
});

export const startRunState = (runId: string): WorkflowRunState => ({
  running: true,
  runId,
  activeNodeId: null,
  completed: {},
  failedNodeId: null,
});

export const applyRunProgress = (
  state: WorkflowRunState,
  progress: AutomationRunProgress,
): WorkflowRunState => {
  if (state.runId && progress.runId !== state.runId) return state;

  switch (progress.status) {
    case 'running':
      return progress.nodeId
        ? {
            ...state,
            running: true,
            runId: state.runId ?? progress.runId,
            activeNodeId: progress.nodeId,
          }
        : state;

    case 'done':
      return progress.nodeId
        ? {
            ...state,
            activeNodeId:
              state.activeNodeId === progress.nodeId
                ? null
                : state.activeNodeId,
            completed: {
              ...state.completed,
              [progress.nodeId]: progress.durationMs ?? 0,
            },
          }
        : state;

    case 'error':
      return {
        ...state,
        running: false,
        activeNodeId: null,
        failedNodeId: progress.nodeId ?? state.failedNodeId,
      };

    default:
      return { ...state, running: false, activeNodeId: null };
  }
};

export const formatStepDuration = (durationMs: number): string =>
  durationMs < 1000
    ? `${Math.round(durationMs)} ms`
    : `${(durationMs / 1000).toFixed(2)} s`;
